import { useState } from "react";
import { ChevronLeft, Loader2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PrivateVideoPlayer from "@/components/video/PrivateVideoPlayer";

function durationLabel(milliseconds) {
  const seconds = Math.round(Number(milliseconds || 0) / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function PrivateVideoFoundationProof() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [scope, setScope] = useState("owner_private");
  const [productId, setProductId] = useState("");
  const [asset, setAsset] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upload = async () => {
    if (!file || busy) return;
    const form = new FormData();
    form.append("file", file);
    form.append("media_type", "video");
    form.append("source_product", "platform_shared");
    form.append("purpose", "private_video_foundation_proof");
    form.append("access_scope", scope);
    if (scope === "product_entitled") form.append("entitlement_product_id", productId.trim());
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/private-media/assets", form);
      setAsset(data);
    } catch (problem) {
      setError(formatApiError(problem));
    } finally { setBusy(false); }
  };

  const metadata = asset?.safe_metadata;
  return <main className="min-h-screen bg-[#F6EFE4] px-4 py-5 sm:px-8 sm:py-8" data-testid="private-video-foundation-proof">
    <div className="mx-auto max-w-5xl">
      <button type="button" onClick={() => nav("/hall")} className="inline-flex min-h-11 items-center gap-2 text-sm text-[#6F5944]"><ChevronLeft size={18} />В главный зал</button>
      <div className="mt-5 max-w-3xl">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[#937556]">Anokina Studio · Owner proof</div>
        <h1 className="mt-2 font-serif text-4xl text-[#2A1D14] sm:text-5xl">Private Video Foundation</h1>
        <p className="mt-4 text-sm leading-6 text-[#66513E]">Загрузи небольшое тестовое MP4-видео, проверь технические metadata и защищённое воспроизведение. Реальные уроки здесь не используются.</p>
      </div>

      <section className="mt-8 rounded-2xl border border-[#E1D2BC] bg-white p-5 sm:p-7">
        <label className="block text-sm font-medium text-[#3C2B20]">Тестовое MP4-видео</label>
        <input type="file" accept="video/mp4" onChange={(event) => setFile(event.target.files?.[0] || null)} className="mt-2 block w-full text-sm" />
        <fieldset className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="rounded-xl border border-[#DDCDB6] p-3 text-sm"><input type="radio" name="scope" value="owner_private" checked={scope === "owner_private"} onChange={(event) => setScope(event.target.value)} /> <span className="ml-2">Owner private</span></label>
          <label className="rounded-xl border border-[#DDCDB6] p-3 text-sm"><input type="radio" name="scope" value="product_entitled" checked={scope === "product_entitled"} onChange={(event) => setScope(event.target.value)} /> <span className="ml-2">Product-entitled</span></label>
        </fieldset>
        {scope === "product_entitled" && <label className="mt-4 block text-sm text-[#3C2B20]">Product ID<input value={productId} onChange={(event) => setProductId(event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border border-[#DDCDB6] px-3" /></label>}
        <button type="button" disabled={!file || busy || (scope === "product_entitled" && !productId.trim())} onClick={upload} className="mt-5 inline-flex min-h-12 items-center gap-2 rounded-xl bg-[#4A3728] px-6 text-sm text-white disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}Загрузить приватно
        </button>
        {error && <div className="mt-4 rounded-xl border border-[#D8A89F] bg-[#FFF4F1] p-4 text-sm text-[#8B382E]" role="alert">{error}</div>}
      </section>

      {asset && <section className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <PrivateVideoPlayer asset={asset} />
        <aside className="rounded-2xl border border-[#E1D2BC] bg-white p-5 text-sm text-[#4E3B2C]">
          <h2 className="font-serif text-2xl text-[#2A1D14]">Metadata</h2>
          <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            <dt>Размер</dt><dd>{asset.size_bytes} bytes</dd>
            <dt>Duration</dt><dd>{durationLabel(metadata?.duration_ms)}</dd>
            <dt>Dimensions</dt><dd>{metadata?.width} × {metadata?.height}</dd>
            <dt>Orientation</dt><dd>{metadata?.orientation}</dd>
            <dt>Aspect ratio</dt><dd>{metadata?.aspect_ratio}</dd>
            <dt>Access</dt><dd>{asset.access_scope}</dd>
          </dl>
          <p className="mt-4 text-xs leading-5 text-[#7A6552]">Storage reference скрыт. Content выдаётся только через authenticated endpoint с private/no-store headers.</p>
        </aside>
      </section>}
    </div>
  </main>;
}
